(() => {
  'use strict';
  if (window.lisaMotionLoaded) return;
  window.lisaMotionLoaded = true;
  const main = document.getElementById('MainContent');
  if (!main || !('IntersectionObserver' in window) || !Element.prototype.animate) return;
  const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const selector = '.merchandise-first__item, #product-grid > .grid__item, .rich-text__blocks';
  const known = new WeakSet();
  const pending = new Set();
  const running = new Map();
  const blocked = () => preference.matches || Boolean(window.Shopify?.designMode);
  const observer = new IntersectionObserver(entries => {
    let stagger = 0;
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      const element = entry.target;
      observer.unobserve(element);
      pending.delete(element);
      if (blocked() || element.contains(document.activeElement)) return;
      const animation = element.animate([
        { opacity: .6, transform: 'translateY(12px)' },
        { opacity: 1, transform: 'translateY(0)' }
      ], {
        duration: 420,
        delay: Math.min(stagger++ * 35, 140),
        easing: 'cubic-bezier(.2,.7,.2,1)',
        fill: 'backwards'
      });
      running.set(element, animation);
      const clear = () => running.delete(element);
      animation.addEventListener('finish', clear, { once: true });
      animation.addEventListener('cancel', clear, { once: true });
    });
  }, { threshold: 0, rootMargin: '0px 0px -12px 0px' });
  function scan() {
    pending.forEach(element => {
      if (!element.isConnected) {
        observer.unobserve(element);
        pending.delete(element);
      }
    });
    main.querySelectorAll(selector).forEach(element => {
      if (known.has(element)) return;
      known.add(element);
      if (blocked() || element.getBoundingClientRect().top < window.innerHeight) return;
      pending.add(element);
      observer.observe(element);
    });
  }
  scan();
  let queued = false;
  const updates = new MutationObserver(records => {
    const relevant = records.some(record => [...record.addedNodes, ...record.removedNodes].some(node =>
      node.nodeType === 1 && (node.matches(selector) || node.querySelector(selector))
    ));
    if (!relevant || queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; scan(); });
  });
  updates.observe(main, { childList: true, subtree: true });
  main.addEventListener('focusin', event => {
    const element = event.target.closest(selector);
    if (!element) return;
    observer.unobserve(element);
    pending.delete(element);
    running.get(element)?.cancel();
  });
  preference.addEventListener('change', () => {
    if (!preference.matches) return;
    observer.disconnect();
    pending.clear();
    running.forEach(animation => animation.cancel());
    running.clear();
  });
})();

(() => {
  'use strict';
  if (window.lisaCartMotionLoaded) return;
  window.lisaCartMotionLoaded = true;
  const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const confirmations = new WeakMap();
  const animations = new Set();
  const flights = new Map();
  function animate(element, frames, options) {
    if (!element || !element.animate || preference.matches) return;
    const animation = element.animate(frames, options);
    animations.add(animation);
    const clear = () => animations.delete(animation);
    animation.addEventListener('finish', clear, { once: true });
    animation.addEventListener('cancel', clear, { once: true });
    return animation;
  }
  function bounceCart(cart) {
    animate(cart?.querySelector('svg'), [
      { transform: 'translateY(0) rotate(0)' },
      { transform: 'translateY(-6px) rotate(-9deg)', offset: .28 },
      { transform: 'translateY(0) rotate(7deg)', offset: .52 },
      { transform: 'translateY(-2px) rotate(-3deg)', offset: .74 },
      { transform: 'translateY(0) rotate(0)' }
    ], { duration: 620, easing: 'ease-out' });
    animate(cart?.querySelector('.cart-count-bubble'), [
      { transform: 'scale(1)' },
      { transform: 'scale(1.3)', offset: .4 },
      { transform: 'scale(1)' }
    ], { duration: 460, easing: 'ease-out' });
  }
  function flyToCart(trigger, cart) {
    if (preference.matches || !trigger?.isConnected || !cart) return false;
    const origin = trigger.getBoundingClientRect();
    const target = cart.getBoundingClientRect();
    if (!origin.width || !target.width || origin.bottom < 0 || origin.top > innerHeight) return false;
    const size = innerWidth < 600 ? 16 : 20;
    const startX = origin.left + origin.width / 2;
    const startY = origin.top + origin.height / 2;
    const endX = Math.max(16, Math.min(innerWidth - 16, target.left + target.width / 2));
    const endY = Math.max(16, Math.min(innerHeight - 16, target.top + target.height / 2));
    const controlX = startX + (endX - startX) * .3;
    const controlY = Math.max(12, Math.min(startY, endY) - 110);
    const thumbnail = document.createElement('span');
    thumbnail.className = 'lisa-cart-flight';
    thumbnail.setAttribute('aria-hidden', 'true');
    thumbnail.style.width = `${size}px`;
    thumbnail.style.height = `${size}px`;
    thumbnail.style.left = `${startX - size / 2}px`;
    thumbnail.style.top = `${startY - size / 2}px`;
    document.body.appendChild(thumbnail);
    const frames = Array.from({ length: 31 }, (_, index) => {
      const t = index / 30;
      const x = (1 - t) ** 2 * startX + 2 * (1 - t) * t * controlX + t ** 2 * endX;
      const y = (1 - t) ** 2 * startY + 2 * (1 - t) * t * controlY + t ** 2 * endY;
      const scale = 1 - .8 * t;
      return {
        offset: t,
        transform: `translate3d(${x - startX}px, ${y - startY}px, 0) scale(${scale}) rotate(${45 + 90 * t}deg)`,
        opacity: t > .85 ? (1 - t) / .15 : 1
      };
    });
    const flight = animate(thumbnail, frames, { duration: 820, easing: 'cubic-bezier(.35,0,.65,1)', fill: 'both' });
    if (!flight) { thumbnail.remove(); return false; }
    const cleanup = () => { thumbnail.remove(); flights.delete(flight); };
    flights.set(flight, thumbnail);
    flight.addEventListener('finish', () => { cleanup(); bounceCart(cart); }, { once: true });
    flight.addEventListener('cancel', cleanup, { once: true });
    return true;
  }
  function cancelFlights() {
    flights.forEach((thumbnail, flight) => flight.cancel());
  }
  window.addEventListener('scroll', cancelFlights, { passive: true });
  window.addEventListener('resize', cancelFlights, { passive: true });
  window.addEventListener('pagehide', cancelFlights);
  document.addEventListener('lisa:cart-added', event => {
    const trigger = event.detail?.trigger;
    if (trigger?.isConnected) {
      const previous = confirmations.get(trigger);
      if (previous) clearTimeout(previous);
      trigger.classList.add('lisa-cart-confirmed');
      let check = trigger.querySelector('.lisa-cart-check');
      if (!check) {
        check = document.createElement('span');
        check.className = 'lisa-cart-check';
        check.setAttribute('aria-hidden', 'true');
        check.textContent = '✓';
        trigger.prepend(check);
      }
      animate(check, [
        { opacity: 0, transform: 'scale(.5)' },
        { opacity: 1, transform: 'scale(1.18)', offset: .65 },
        { opacity: 1, transform: 'scale(1)' }
      ], { duration: 320, easing: 'ease-out' });
      confirmations.set(trigger, setTimeout(() => {
        trigger.classList.remove('lisa-cart-confirmed');
        check.remove();
        confirmations.delete(trigger);
      }, 1800));
    }
    requestAnimationFrame(() => {
      const cart = document.getElementById('cart-icon-bubble');
      if (!flyToCart(trigger, cart)) bounceCart(cart);
    });
  });
  preference.addEventListener('change', () => {
    if (!preference.matches) return;
    animations.forEach(animation => animation.cancel());
    animations.clear();
  });
})();
